/**
 * ProfileImageCropperModal Component
 *
 * Modal for cropping profile images with pan and zoom functionality using react-easy-crop.
 * Features:
 * - Drag to pan image
 * - Slider to zoom in/out (50% - 200%)
 * - Upload new image button
 * - Circular image container (1:1 aspect ratio)
 * - Centered title and description
 */

import { X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import Cropper from "react-easy-crop"
import { UploadLinkIcon } from "@/components/icons/index"
import { Button } from "@/components/ui/button"

interface ProfileImageCropperModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  imageFile: File | null
  onCropComplete: (croppedFile: File) => void
}

const MIME_TO_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
}

interface CropArea {
  x: number
  y: number
  width: number
  height: number
}

export function ProfileImageCropperModal({
  open,
  onOpenChange,
  imageFile,
  onCropComplete,
}: ProfileImageCropperModalProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [isProcessing, setIsProcessing] = useState(false)
  const [minZoom, setMinZoom] = useState(1)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open || !imageFile) {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc)
        setImageSrc(null)
      }
      setCrop({ x: 0, y: 0 })
      setZoom(1)
      setMinZoom(1)
      return
    }
  }, [open, imageFile, imageSrc])

  useEffect(() => {
    if (imageFile && open) {
      const url = URL.createObjectURL(imageFile)
      setImageSrc(url)

      const img = new window.Image()
      img.onload = () => {
        const containerSize = 350
        const scale = containerSize / Math.min(img.width, img.height)
        setMinZoom(scale)
        setZoom(scale)
      }
      img.src = url

      return () => {
        URL.revokeObjectURL(url)
      }
    }
  }, [imageFile, open])

  const handleCropComplete = useCallback(
    (_croppedArea: CropArea, _croppedAreaPixels: CropArea) => {},
    [],
  )

  const handleReplaceImage = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        if (imageSrc) {
          URL.revokeObjectURL(imageSrc)
        }
        const url = URL.createObjectURL(file)
        setImageSrc(url)
        setCrop({ x: 0, y: 0 })

        const img = new window.Image()
        img.onload = () => {
          const containerSize = 350
          const scale = containerSize / Math.min(img.width, img.height)
          setMinZoom(scale)
          setZoom(scale)
        }
        img.src = url
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    },
    [imageSrc],
  )

  const createCroppedImage = useCallback(
    async (crop: { x: number; y: number }, zoom: number) => {
      if (!imageFile || !imageSrc) return null

      const img = new Image()
      img.crossOrigin = "anonymous"

      return new Promise<File>((resolve, reject) => {
        img.onload = () => {
          const canvas = document.createElement("canvas")
          const ctx = canvas.getContext("2d")
          if (!ctx) {
            reject(new Error("No 2d context"))
            return
          }

          const outputSize = 800
          const containerSize = 350

          canvas.width = outputSize
          canvas.height = outputSize

          ctx.fillStyle = "#000000"
          ctx.fillRect(0, 0, outputSize, outputSize)

          const containScale = Math.min(containerSize / img.width, containerSize / img.height)
          const effectiveScale = containScale * zoom

          const imgRenderW = img.width * effectiveScale
          const imgRenderH = img.height * effectiveScale

          const imgLeft = containerSize / 2 + crop.x - imgRenderW / 2
          const imgTop = containerSize / 2 + crop.y - imgRenderH / 2

          const scale = outputSize / containerSize

          ctx.drawImage(
            img,
            imgLeft * scale,
            imgTop * scale,
            imgRenderW * scale,
            imgRenderH * scale,
          )

          canvas.toBlob(
            blob => {
              if (!blob) {
                reject(new Error("Canvas is empty"))
                return
              }
              const extension = MIME_TO_EXT[imageFile.type] || "jpg"
              const croppedFile = new File(
                [blob],
                imageFile.name.replace(/\.[^.]+$/, `-cropped.${extension}`),
                {
                  type: imageFile.type,
                },
              )
              resolve(croppedFile)
            },
            imageFile.type,
            0.95,
          )
        }

        img.onerror = () => reject(new Error("Failed to load image"))
        img.src = imageSrc
      })
    },
    [imageFile, imageSrc],
  )

  const handleApply = useCallback(async () => {
    setIsProcessing(true)
    try {
      const croppedFile = await createCroppedImage(crop, zoom)
      if (croppedFile) {
        onCropComplete(croppedFile)
        onOpenChange(false)
      }
    } catch (error) {
      console.error("Crop failed:", error)
    } finally {
      setIsProcessing(false)
    }
  }, [crop, zoom, createCroppedImage, onCropComplete, onOpenChange])

  const handleClose = useCallback(() => {
    if (imageSrc) {
      URL.revokeObjectURL(imageSrc)
      setImageSrc(null)
    }
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setMinZoom(1)
    onOpenChange(false)
  }, [imageSrc, onOpenChange])

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom)
  }, [])

  const onCropChange = useCallback((crop: { x: number; y: number }) => {
    setCrop(crop)
  }, [])

  if (!open || !imageFile || !imageSrc) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="fixed inset-0 bg-[rgba(0,0,0,0.9)]" onClick={handleClose} />
      <div className="relative z-10 flex w-full max-w-[600px] flex-col  rounded-tr-2xl  rounded-bl-2xl border bg-[#1C1C1E] p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-[21px] font-semibold text-white text-center flex-1 mt-4">
            Center Your Profile Picture
          </h2>
          <button
            onClick={handleClose}
            className="cursor-pointer rounded-full border border-input p-2 hover:bg-white/10 transition-colors absolute right-4 top-4"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <p className="mb-4 text-[13px] text-[#A1A1A6] text-center">
          Upload an image and adjust its position and zoom to get the perfect profile picture.
        </p>

        <div className="relative mb-4 flex items-center justify-center">
          <div className="h-[350px] w-[350px] overflow-hidden rounded-full bg-black">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              minZoom={minZoom}
              maxZoom={3}
              aspect={1}
              cropShape="round"
              objectFit="contain"
              onCropChange={onCropChange}
              onCropComplete={handleCropComplete}
              onZoomChange={onZoomChange}
              style={{
                containerStyle: {
                  width: "100%",
                  height: "100%",
                  backgroundColor: "#000000",
                },
                mediaStyle: {
                  backgroundColor: "#000000",
                },
              }}
            />
          </div>
        </div>

        <div className="mb-6 flex items-center gap-4">
          <input
            type="range"
            min={minZoom}
            max="3"
            step="0.1"
            value={zoom}
            onChange={e => onZoomChange(parseFloat(e.target.value))}
            className="flex-1 h-1 bg-[#38383A] rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-terracotta"
          />
        </div>

        <div className="mb-6 flex flex-row gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            onChange={handleFileChange}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={handleReplaceImage}
            className="flex-1 flex items-center justify-center gap-2 text-sm text-[#0A84FF] hover:text-[#0A84FF]/80 transition-colors uppercase"
          >
            <UploadLinkIcon className="size-4" />
            Upload New Image
          </Button>
          <Button onClick={handleApply} disabled={isProcessing} className="flex-1 uppercase">
            {isProcessing ? "Processing..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  )
}
